import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import Icon from '../components/Icon';

export default function LogSession() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [values, setValues] = useState({});
  const [lastValues, setLastValues] = useState({});
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const startTime = useRef(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - startTime.current) / 1000)), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    Promise.all([
      api.get('/clients/' + id),
      api.get('/clients/' + id + '/metrics'),
      api.get('/clients/' + id + '/sessions'),
    ]).then(([c, m, sessions]) => {
      setClient(c);
      setMetrics(m);
      // populate last values from most recent session
      if (sessions && sessions.length > 0) {
        const last = sessions[0];
        const lv = {};
        if (last.metrics) {
          last.metrics.forEach(sm => { lv[sm.metric_id] = sm.value; });
        }
        setLastValues(lv);
      }
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  function formatTime(secs) {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return m + ':' + s;
  }

  async function handleSave() {
    setSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const metricsPayload = metrics
        .filter(m => values[m.id] !== undefined && values[m.id] !== '')
        .map(m => ({ metric_id: m.id, value: parseFloat(values[m.id]) }));
      await api.post('/sessions', {
        client_id: parseInt(id),
        date: today,
        notes,
        metrics: metricsPayload,
      });
      setSaved(true);
    } catch (err) {
      alert(err.message);
      setSaving(false);
    }
  }

  if (loading) return <div className="loading-spinner" style={{ minHeight: '100vh' }}><span className="spinner" /></div>;

  if (saved) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--color-bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, gap: 16 }}>
        <Icon name="icon-check.png" size={80} alt="Done" />
        <h2 style={{ margin: 0, fontSize: 24 }}>Session Saved!</h2>
        <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>Duration: {formatTime(elapsed)}</p>
        <div style={{ display: 'flex', gap: 12, width: '100%', maxWidth: 360 }}>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => navigate('/clients/' + id)}>
            View Profile
          </button>
          <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => navigate('/clients')}>
            All Clients
          </button>
        </div>
      </div>
    );
  }

  if (metrics.length === 0) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--color-bg)', display: 'flex', flexDirection: 'column' }}>
        <div className="page-header">
          <button className="icon-btn" onClick={() => navigate('/clients/' + id)}>
            <Icon name="icon-back.png" alt="Back" />
          </button>
          <h1 style={{ flex: 1 }}>Log Session</h1>
        </div>
        <div className="empty-state" style={{ flex: 1 }}>
          <p>No tracked metrics yet.</p>
          <button className="btn btn-primary" style={{ width: 'auto', padding: '0 24px' }}
            onClick={() => navigate('/clients/' + id + '?tab=Metrics')}>
            Add Metrics First
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)', display: 'flex', flexDirection: 'column' }}>
      <div className="page-header">
        <button className="icon-btn" onClick={() => navigate('/clients/' + id)}>
          <Icon name="icon-back.png" alt="Back" />
        </button>
        <h1 style={{ flex: 1, fontSize: 17 }}>{client?.first_name} {client?.last_name}</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-text-muted)', fontSize: 14 }}>
          <Icon name="icon-clock.png" size={16} />
          {formatTime(elapsed)}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 120px' }}>
        {metrics.map(m => (
          <div key={m.id} className="card" style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{m.exercise_name}</div>
            {lastValues[m.id] !== undefined && (
              <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 8 }}>
                Last: {lastValues[m.id]}{m.unit ? ' ' + m.unit : ''}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="number"
                step="any"
                value={values[m.id] || ''}
                onChange={(e) => setValues(v => ({ ...v, [m.id]: e.target.value }))}
                placeholder="Enter value"
                style={{
                  flex: 1, background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
                  borderRadius: 8, color: 'var(--color-text)', fontFamily: 'var(--font)',
                  fontSize: 28, fontWeight: 700, minHeight: 64, padding: '0 16px', outline: 'none',
                  width: '100%',
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--color-primary)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
              />
              {m.unit && <span style={{ fontSize: 16, color: 'var(--color-text-muted)', flexShrink: 0 }}>{m.unit}</span>}
            </div>
          </div>
        ))}

        <div className="form-group" style={{ marginTop: 8 }}>
          <label className="form-label">Session Notes (optional)</label>
          <textarea className="form-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any notes for this session..." />
        </div>
      </div>

      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, padding: '12px 16px', background: 'var(--color-surface)', borderTop: '1px solid var(--color-border)' }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving
            ? <><span className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Saving...</>
            : <><Icon name="icon-save.png" size={20} /> Save Session</>
          }
        </button>
      </div>
    </div>
  );
}
